// /opt/ptg01-v1/front-extranet/components/DashboardContent.tsx
// Dashboard consultant — 4 bandes :
//   1. Hero + 2 CTA majeurs (Déclarer CRA, Ajouter dépense)
//   2. Synthèse financière (4 KPIs : dispo, prochain versement, revenus mois, frais attente)
//   3. Lignes bancaires récentes (style relevé bancaire moderne)
//   4. États & alertes (CRA / Dépenses / Docs) + actions rapides
//
// Data : mockée ici pour le moment. On branchera les vraies API au fur et a mesure.
// Design : pattern admin (bg-white + border-2 border-[#000091]/25 + rounded-2xl).
'use client';

import React, { useMemo, useState } from 'react';
import Link from 'next/link';
import {
    CalendarCheck, Receipt, ArrowRight, Wallet, TrendingUp,
    FileText, Search, Plus, Download, MessageCircle, Clock, Circle,
} from 'lucide-react';

// ─────────────────────── Types mockés (a brancher API plus tard) ───────────────────────

import {
    type BankLineStatus,
    BANK_LINE_LABEL,
    getBankLineStyle,
} from '@/lib/status';

type LineType = 'salaire' | 'facture' | 'remboursement' | 'cotisations' | 'ticket' | 'autre';
type LineStatus = BankLineStatus;
interface BankLine {
    id: string;
    date: string;      // ISO
    label: string;
    type: LineType;
    status: LineStatus;
    amount: number;    // positif = entrée, négatif = sortie
}

const MOCK_LINES: BankLine[] = [
    { id: '1', date: '2026-03-05', label: 'Salaire février — virement SEPA', type: 'salaire', status: 'recu', amount: 4650.00 },
    { id: '2', date: '2026-03-03', label: 'Facture client ACME Corp — règlement', type: 'facture', status: 'percu', amount: 12000.00 },
    { id: '3', date: '2026-02-28', label: 'Remboursement frais (note #142)', type: 'remboursement', status: 'rembourse', amount: 187.40 },
    { id: '4', date: '2026-02-25', label: 'Cotisations URSSAF', type: 'cotisations', status: 'en_cours', amount: -1820.50 },
    { id: '5', date: '2026-02-20', label: 'Titres-restaurant (10 titres × 9,50€)', type: 'ticket', status: 'attribue', amount: 95.00 },
];

// STATUS_META centralisé dans lib/status.ts (BANK_LINE_LABEL + getBankLineStyle).

const TYPE_ICON: Record<LineType, React.ReactNode> = {
    salaire:       <Wallet className="w-3.5 h-3.5 text-[#000091]" />,
    facture:       <TrendingUp className="w-3.5 h-3.5 text-[#000091]" />,
    remboursement: <Receipt className="w-3.5 h-3.5 text-[#000091]" />,
    cotisations:   <FileText className="w-3.5 h-3.5 text-slate-500" />,
    ticket:        <Circle className="w-3.5 h-3.5 text-[#000091]" />,
    autre:         <Circle className="w-3.5 h-3.5 text-slate-400" />,
};

function formatDateShort(iso: string): string {
    const d = new Date(iso + 'T00:00:00Z');
    const months = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
    return `${String(d.getUTCDate()).padStart(2, '0')} ${months[d.getUTCMonth()]}`;
}
function formatAmount(n: number): string {
    const abs = Math.abs(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${n > 0 ? '+' : n < 0 ? '−' : ''}${abs} €`;
}

// ─────────────────────── Main component ───────────────────────

export default function DashboardContent() {
    const [search, setSearch] = useState('');
    const [period, setPeriod] = useState<'30' | '90' | 'all'>('30');

    const filteredLines = useMemo(() => {
        const q = search.trim().toLowerCase();
        return MOCK_LINES.filter(l => !q || l.label.toLowerCase().includes(q));
    }, [search]);

    // Cadence dynamique calculée à partir de la date du jour
    const today = new Date();
    const currentMonthIdx = today.getMonth();           // 0-11
    const currentYear = today.getFullYear();
    const lastDayOfMonth = new Date(currentYear, currentMonthIdx + 1, 0).getDate();   // 28/29/30/31
    const nextMonthIdx = (currentMonthIdx + 1) % 12;
    const monthNamesLong = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
    const currentMonthName = monthNamesLong[currentMonthIdx];
    const nextMonthName = monthNamesLong[nextMonthIdx];

    // Calcul d'urgence : deadline ≤ 5 jours = badge rouge (sinon bleu Nexvia)
    const daysUntil = (day: number) => {
        const target = new Date(currentYear, currentMonthIdx, day);
        return Math.ceil((target.getTime() - today.getTime()) / 86400000);
    };
    const expenseUrgent = daysUntil(lastDayOfMonth) <= 5 && daysUntil(lastDayOfMonth) >= 0;
    const craUrgent = daysUntil(25) <= 5 && daysUntil(25) >= 0;
    const badgeClasses = (urgent: boolean) => urgent
        ? 'bg-red-50 border-red-300 text-red-700'
        : 'bg-[#000091]/[0.08] border-[#000091]/25 text-[#000091]';

    // Période d'établissement du salaire = 1er au 5 du mois suivant la mission.
    // Hors de cette fenêtre, la CTA « Déclarer mon salaire » est grisée.
    const salaryWindowStart = new Date(currentYear, nextMonthIdx, 1);
    const salaryWindowEnd = new Date(currentYear, nextMonthIdx, 5, 23, 59);
    const salaryInPeriod = today >= salaryWindowStart && today <= salaryWindowEnd;

    return (
        <div className="px-4 lg:px-8 py-4 lg:py-6 space-y-4 lg:space-y-6 max-w-7xl mx-auto">

            {/* ═══════════ BANDE 1 — Hero + CTA ═══════════ */}
            <section>
                <h1 className="text-[24px] font-black text-slate-900 leading-tight tracking-tight">
                    Bonjour <span className="text-[#000091]">Jean</span>
                </h1>
                <p className="text-[13px] text-slate-500 mt-1">Voici la situation de votre compte d’activité pour le mois en cours.</p>

                {/* 3 CTA toujours sur la même ligne (grid-cols-3 même <md).
                    Mobile : padding réduit, deadline + ArrowRight masqués pour tenir. */}
                <div className="mt-5 grid grid-cols-3 gap-2 md:gap-4">
                    {/* 1. Frais professionnels — deadline = dernier jour du mois courant */}
                    <Link
                        href="/depenses/creer"
                        className="group bg-white hover:bg-[#000091]/[0.03] border-2 border-[#000091]/25 hover:border-[#000091]/40 rounded-2xl p-3 md:p-5 transition-colors"
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                                    <Receipt className="w-4 h-4 md:w-5 md:h-5 text-[#000091] shrink-0" strokeWidth={2} />
                                    <span className="text-[8px] md:text-[10px] font-black uppercase tracking-wider text-[#000091] truncate">{currentMonthName} {currentYear}</span>
                                </div>
                                <div className="text-[12px] md:text-[17px] font-black text-slate-900 leading-tight">Ajouter une note de frais</div>
                                <div className={`mt-1.5 md:mt-2 flex flex-col items-center justify-center w-full px-1.5 md:px-2 py-1 md:py-1.5 rounded border ${badgeClasses(expenseUrgent)}`}>
                                    <span className="text-[7px] md:text-[9px] font-bold uppercase tracking-wider opacity-80 leading-none">
                                        Date limite
                                    </span>
                                    <span className="text-[10px] md:text-[12px] font-black tabular-nums leading-tight mt-0.5">
                                        {lastDayOfMonth} {currentMonthName}
                                    </span>
                                </div>
                                <div className="hidden md:block mt-3 pt-2.5 border-t border-[#000091]/10 space-y-1">
                                    <div className="flex items-center justify-between text-[11px]">
                                        <span className="text-slate-500">Soumis ce mois</span>
                                        <span className="font-bold text-slate-900 tabular-nums">230 €</span>
                                    </div>
                                    <div className="flex items-center justify-between text-[11px]">
                                        <span className="text-slate-500">Refacturables</span>
                                        <span className="font-bold text-slate-900 tabular-nums">480 €</span>
                                    </div>
                                </div>
                            </div>
                            <ArrowRight className="hidden md:block w-5 h-5 text-[#000091] group-hover:translate-x-1 transition-transform mt-1 shrink-0" strokeWidth={2.5} />
                        </div>
                    </Link>

                    {/* 2. CRA — consultant 25 → validation admin = dernier jour.
                        Lien vers /cras (SplitView) : la page auto-sélectionne le 1er CRA actif. */}
                    <Link
                        href="/cras"
                        className="group bg-white hover:bg-[#000091]/[0.03] border-2 border-[#000091]/25 hover:border-[#000091]/40 rounded-2xl p-3 md:p-5 transition-colors"
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                                    <CalendarCheck className="w-4 h-4 md:w-5 md:h-5 text-[#000091] shrink-0" strokeWidth={2} />
                                    <span className="text-[8px] md:text-[10px] font-black uppercase tracking-wider text-[#000091] truncate">{currentMonthName} {currentYear}</span>
                                </div>
                                <div className="text-[12px] md:text-[17px] font-black text-slate-900 leading-tight">Valider mon CRA</div>
                                <div className={`mt-1.5 md:mt-2 flex flex-col items-center justify-center w-full px-1.5 md:px-2 py-1 md:py-1.5 rounded border ${badgeClasses(craUrgent)}`}>
                                    <span className="text-[7px] md:text-[9px] font-bold uppercase tracking-wider opacity-80 leading-none">
                                        Date limite
                                    </span>
                                    <span className="text-[10px] md:text-[12px] font-black tabular-nums leading-tight mt-0.5">
                                        25 {currentMonthName}
                                    </span>
                                </div>
                                <div className="hidden md:block mt-3 pt-2.5 border-t border-[#000091]/10 space-y-1">
                                    <div className="flex items-center justify-between text-[11px]">
                                        <span className="text-slate-500">Jours saisis</span>
                                        <span className="font-bold text-slate-900 tabular-nums">18 / 22</span>
                                    </div>
                                    <div className="flex items-center justify-between text-[11px]">
                                        <span className="text-slate-500">Validation admin</span>
                                        <span className="font-bold text-slate-900 tabular-nums">{lastDayOfMonth} {currentMonthName}</span>
                                    </div>
                                </div>
                            </div>
                            <ArrowRight className="hidden md:block w-5 h-5 text-[#000091] group-hover:translate-x-1 transition-transform mt-1 shrink-0" strokeWidth={2.5} />
                        </div>
                    </Link>

                    {/* 3. Salaire — établi 1er-5 du mois suivant. Grisé hors période. */}
                    <Link
                        href={salaryInPeriod ? '/finance/salaires/declarer' : '#'}
                        aria-disabled={!salaryInPeriod}
                        onClick={salaryInPeriod ? undefined : (e) => e.preventDefault()}
                        title={salaryInPeriod ? undefined : `Disponible du 1er au 5 ${nextMonthName} ${currentYear}`}
                        className={`group rounded-2xl p-3 md:p-5 transition-colors border-2 ${
                            salaryInPeriod
                                ? 'bg-white hover:bg-[#000091]/[0.03] border-[#000091]/25 hover:border-[#000091]/40'
                                : 'bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed'
                        }`}
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 md:gap-2 mb-1.5 md:mb-2">
                                    <Wallet className={`w-4 h-4 md:w-5 md:h-5 shrink-0 ${salaryInPeriod ? 'text-[#000091]' : 'text-slate-400'}`} strokeWidth={2} />
                                    <span className={`text-[8px] md:text-[10px] font-black uppercase tracking-wider truncate ${salaryInPeriod ? 'text-[#000091]' : 'text-slate-400'}`}>{currentMonthName} {currentYear}</span>
                                </div>
                                <div className={`text-[12px] md:text-[17px] font-black leading-tight ${salaryInPeriod ? 'text-slate-900' : 'text-slate-500'}`}>Déclarer mon salaire</div>
                                <div className={`mt-1.5 md:mt-2 flex flex-col items-center justify-center w-full px-1.5 md:px-2 py-1 md:py-1.5 rounded border ${
                                    salaryInPeriod
                                        ? 'bg-[#000091]/[0.08] border-[#000091]/25 text-[#000091]'
                                        : 'bg-slate-100 border-slate-200 text-slate-500'
                                }`}>
                                    <span className="text-[7px] md:text-[9px] font-bold uppercase tracking-wider opacity-80 leading-none">
                                        Ouverture
                                    </span>
                                    <span className="text-[10px] md:text-[12px] font-black tabular-nums leading-tight mt-0.5">
                                        1-5 {nextMonthName}
                                    </span>
                                </div>
                                <div className="hidden md:block mt-3 pt-2.5 border-t border-[#000091]/10 space-y-1">
                                    <div className="flex items-center justify-between text-[11px]">
                                        <span className="text-slate-500">Net estimé</span>
                                        <span className={`font-bold tabular-nums ${salaryInPeriod ? 'text-slate-900' : 'text-slate-500'}`}>4 850 €</span>
                                    </div>
                                    <div className="flex items-center justify-between text-[11px]">
                                        <span className="text-slate-500">Virement</span>
                                        <span className={`font-bold tabular-nums ${salaryInPeriod ? 'text-slate-900' : 'text-slate-500'}`}>5 {nextMonthName}</span>
                                    </div>
                                </div>
                            </div>
                            <ArrowRight className={`hidden md:block w-5 h-5 transition-transform mt-1 shrink-0 ${salaryInPeriod ? 'text-[#000091] group-hover:translate-x-1' : 'text-slate-300'}`} strokeWidth={2.5} />
                        </div>
                    </Link>
                </div>
            </section>

            {/* ═══════════ BANDE 2 — 4 KPIs financiers ═══════════ */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Kpi label="Disponible"           value="2 340 €" hint="net à venir" />
                <Kpi label="Prochain versement"   value="4 850 €" hint="5 avril" accent />
                <Kpi label="Revenus mois"         value="4 850 €" hint="+2,4 % vs févr." trend="up" />
                <Kpi label="Frais en attente"     value="230 €"   hint="2 notes" />
            </section>

            {/* ═══════════ BANDE 3 — Lignes bancaires (différenciateur) ═══════════ */}
            <section>
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                    <h2 className="text-[15px] font-black text-slate-900">Mouvements récents</h2>
                    <Link href="/compte-activite" className="text-[11px] font-semibold text-[#000091] hover:underline flex items-center gap-1">
                        Tout voir <ArrowRight className="w-3 h-3" />
                    </Link>
                </div>

                <div className="bg-white border-2 border-[#000091]/25 rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden">
                    <div className="px-4 py-3 border-b border-[#000091]/10 flex items-center gap-3 flex-wrap">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                            <input
                                type="text"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Rechercher un mouvement…"
                                className="w-full pl-8 pr-3 h-8 text-[12px] bg-white border border-slate-200 rounded-md focus:border-[#000091] focus:outline-none focus:ring-1 focus:ring-[#000091]/20"
                            />
                        </div>
                        <select
                            value={period}
                            onChange={e => setPeriod(e.target.value as '30' | '90' | 'all')}
                            className="h-8 px-2.5 text-[12px] bg-white border border-slate-200 rounded-md focus:border-[#000091] focus:outline-none"
                        >
                            <option value="30">30 derniers jours</option>
                            <option value="90">90 derniers jours</option>
                            <option value="all">Tout l’historique</option>
                        </select>
                    </div>

                    <div className="divide-y divide-[#000091]/10">
                        {filteredLines.length === 0 ? (
                            <div className="px-4 py-8 text-center text-[12px] text-slate-400">Aucun mouvement trouvé</div>
                        ) : filteredLines.map(line => {
                            const style = getBankLineStyle(line.status);
                            const positive = line.amount > 0;
                            return (
                                <div
                                    key={line.id}
                                    className="px-4 py-3 flex items-center gap-4 hover:bg-[#000091]/[0.02] transition-colors cursor-pointer"
                                >
                                    <div className="w-8 h-8 rounded-lg bg-[#000091]/[0.06] flex items-center justify-center shrink-0">
                                        {TYPE_ICON[line.type]}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <span className="text-[11px] text-slate-500 tabular-nums">{formatDateShort(line.date)}</span>
                                            <span className={`inline-flex items-center text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${style.bg} ${style.text}`}>
                                                {line.status === 'en_cours' && <Clock className="w-2.5 h-2.5 mr-0.5" />}
                                                {BANK_LINE_LABEL[line.status]}
                                            </span>
                                        </div>
                                        <div className="text-[13px] font-semibold text-slate-900 truncate">{line.label}</div>
                                    </div>
                                    <div className={`text-[15px] font-bold tabular-nums shrink-0 ${positive ? 'text-green-700' : 'text-slate-900'}`}>
                                        {formatAmount(line.amount)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ═══════════ BANDE 4 — États & actions rapides ═══════════ */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatusCard
                    icon={<CalendarCheck className="w-4 h-4 text-[#000091]" />}
                    title="CRA du mois"
                    value="Brouillon"
                    detail="18 / 22 jours saisis"
                    cta={{ href: `/cras/${currentYear}/${currentMonthIdx + 1}`, label: 'Compléter' }}
                />
                <StatusCard
                    icon={<Receipt className="w-4 h-4 text-[#000091]" />}
                    title="Dépenses"
                    value="3 en attente"
                    detail="2 validées · 5 remboursées"
                    cta={{ href: '/depenses', label: 'Gérer' }}
                />
                <StatusCard
                    icon={<FileText className="w-4 h-4 text-[#000091]" />}
                    title="Documents"
                    value="1 à signer"
                    detail="Contrat mission ACME"
                    cta={{ href: '/documents/contrats', label: 'Signer' }}
                    alert
                />
            </section>

            <section>
                <h2 className="text-[11px] font-bold uppercase tracking-wider text-[#000091] mb-2">Actions rapides</h2>
                <div className="flex flex-wrap gap-2">
                    <QuickAction href="/depenses/notes" icon={<Plus className="w-3.5 h-3.5" />}>Nouvelle note de frais</QuickAction>
                    <QuickAction href="/documents/paie" icon={<Download className="w-3.5 h-3.5" />}>Télécharger bulletin</QuickAction>
                    <QuickAction href="/mon-compte/demarches" icon={<MessageCircle className="w-3.5 h-3.5" />}>Contacter mon référent</QuickAction>
                </div>
            </section>
        </div>
    );
}

// ─────────────────────── Primitives ───────────────────────

function Kpi({ label, value, hint, accent = false, trend }: {
    label: string; value: string; hint?: string; accent?: boolean; trend?: 'up' | 'down';
}) {
    return (
        <div className={`bg-white border-2 rounded-2xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)] ${
            accent ? 'border-[#000091]/40 bg-[#000091]/[0.02]' : 'border-[#000091]/25'
        }`}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#000091]/80">{label}</div>
            <div className="text-[22px] font-black text-slate-900 leading-tight tabular-nums mt-1">{value}</div>
            {hint && (
                <div className={`text-[11px] mt-1 ${trend === 'up' ? 'text-green-700 font-semibold' : 'text-slate-500'}`}>
                    {trend === 'up' && <TrendingUp className="w-3 h-3 inline mr-0.5" />}
                    {hint}
                </div>
            )}
        </div>
    );
}

function StatusCard({ icon, title, value, detail, cta, alert = false }: {
    icon: React.ReactNode; title: string; value: string; detail: string;
    cta: { href: string; label: string }; alert?: boolean;
}) {
    return (
        <div className={`bg-white border-2 rounded-2xl p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)] ${
            alert ? 'border-amber-300' : 'border-[#000091]/25'
        }`}>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    {icon}
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{title}</span>
                </div>
                {alert && <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />}
            </div>
            <div className="text-[15px] font-black text-slate-900 leading-tight">{value}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">{detail}</div>
            <Link href={cta.href} className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#000091] hover:underline mt-3">
                {cta.label} <ArrowRight className="w-3 h-3" />
            </Link>
        </div>
    );
}

function QuickAction({ href, icon, children }: { href: string; icon: React.ReactNode; children: React.ReactNode }) {
    return (
        <Link
            href={href}
            className="inline-flex items-center gap-1.5 h-9 px-3 text-[12px] font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg hover:border-[#000091]/40 hover:bg-[#000091]/[0.03] hover:text-[#000091] transition-colors"
        >
            {icon}
            {children}
        </Link>
    );
}
